import {
  DRAIN_MAX_ATTEMPTS,
  DRAIN_PULSE_TARGET_MS,
  DRAIN_VERIFY_MS,
  STANDING_LATENCY_DRAIN_MESSAGE_TYPE,
  advanceDrainLadder,
  initialDrainLadderState,
  isDrainSuccess,
  judgeDrainStep,
  nextDrainStep,
  nextLowerFpsRung,
  type DrainAction,
  type DrainLadderState,
  type DrainOutcome,
  type DrainStep,
  type ReceiverHintReadback,
  type StandingLatencyDrainRequest,
  type StandingLatencyDrainResult,
  type StandingLatencyTransition,
  type StandingLatencyVerdict,
} from '@playcastdotio/PeerCore';

export type DrainEncoding = { maxBitrateBps: number; maxFramerate: number };

/** `StreamHealthEvent.detail` values: primitives only, absent rather than zero-filled. */
export type DrainHealthDetail = Record<string, number | string | boolean>;

/**
 * Everything this manager can do to the world. Deliberately narrow: two channels to the guest
 * (a datachannel message and the encoder's fps/bitrate override), two channels to the record.
 * No handle to the connection, the track, the transceiver or the decoder exists here, so no
 * drain lever can reconnect, renegotiate, restart ICE, replace a track, reload or reset a
 * decoder. Tests assert this surface is exactly these five functions.
 */
export type StandingLatencyDrainDeps = {
  send: (peerId: string, json: string) => boolean;
  applyEncoding: (peerId: string, opts: DrainEncoding) => void;
  currentEncoding: (peerId: string) => DrainEncoding | undefined;
  emitHealthEvent: (peerId: string, kind: 'drain', detail: DrainHealthDetail) => void;
  debugLog: (tag: '[Drain]', fields: Record<string, unknown>) => void;
  verifyMs?: number;
  maxAttempts?: number;
  nowMs?: () => number;
  setTimeout?: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout?: (timer: ReturnType<typeof setTimeout>) => void;
};

type DepthSnapshot = { excessMs: number | null; depthFrames: number | null; depthMs: number | null };

type DrainEpisode = {
  episodeId: string;
  ladder: DrainLadderState;
  latestVerdict: StandingLatencyVerdict;
  /** Standing depth beside the latest verdict, from the diagnostics manager's tick. */
  latestDepth: { frames: number | null; ms: number | null };
  /** The step in flight, or null between steps and after the ladder has finished. */
  step: DrainStep | null;
  /** Host-assigned seq of the hint request awaiting its result. */
  seq: number | null;
  result: StandingLatencyDrainResult | null;
  before: DepthSnapshot;
  /** The encoding to put back once the fps dip's verify window elapses. */
  dipRestore: DrainEncoding | null;
  dip: { fromFps: number; toFps: number } | null;
  verifyTimer: ReturnType<typeof setTimeout> | null;
  /** True once the ladder cleared, improved or exhausted; nothing further is attempted. */
  finished: boolean;
};

/**
 * StandingLatencyDrainManager — drains a standing receive-side buffer without a hitch.
 *
 * On a `standing-buffer` `began` transition it climbs the drain ladder: re-assert the guest's
 * receiver hints, pulse them, then (only when the depth is time-constant) dip the host's
 * maxFramerate one rung for the verify window so playout can run ahead of arrival. Each step
 * is judged after `verifyMs` against the verdict Phase 3 keeps reporting: cleared, improved
 * (excess halved), no effect, or not applied. One `[Drain]` line and one `drain` health event
 * per step; `exhausted` once when the ladder ends without success. One ladder per episode;
 * `ended` cancels whatever is pending and restores any dip.
 */
export class StandingLatencyDrainManager {
  private readonly episodes = new Map<string, DrainEpisode>();
  private readonly verifyMs: number;
  private readonly maxAttempts: number;
  private readonly nowMs: () => number;
  private readonly schedule: (callback: () => void, ms: number) => ReturnType<typeof setTimeout>;
  private readonly cancel: (timer: ReturnType<typeof setTimeout>) => void;
  private episodeCounter = 0;
  private seqCounter = 0;

  constructor(private readonly deps: StandingLatencyDrainDeps) {
    this.verifyMs = deps.verifyMs ?? DRAIN_VERIFY_MS;
    this.maxAttempts = deps.maxAttempts ?? DRAIN_MAX_ATTEMPTS;
    this.nowMs = deps.nowMs ?? (() => Date.now());
    this.schedule = deps.setTimeout ?? ((callback, ms) => setTimeout(callback, ms));
    this.cancel = deps.clearTimeout ?? ((timer) => clearTimeout(timer));
  }

  /** The Phase 3 transition for this tick. `began` starts a ladder; `ended` closes it. */
  public onStandingTransition(
    peerId: string,
    verdict: StandingLatencyVerdict,
    transition: StandingLatencyTransition,
    depth: { frames: number | null; ms: number | null } = { frames: null, ms: null },
  ): void {
    const episode = this.episodes.get(peerId);
    if (episode) {
      episode.latestVerdict = verdict;
      episode.latestDepth = depth;
    }
    if (verdict.evidenceState === 'unavailable' || verdict.evidenceState === 'tentative') {
      if (episode) this.closeEpisode(peerId, episode);
      return;
    }
    if (transition === 'began' && verdict.kind === 'standing-buffer' && verdict.evidenceState === 'confirmed') {
      if (episode) this.closeEpisode(peerId, episode);
      this.beginEpisode(peerId, verdict, depth);
      return;
    }
    if ((transition === 'ended' || transition === 'clear') && episode) {
      if (episode.step !== null) this.finishStep(peerId, episode, true);
      this.closeEpisode(peerId, episode);
    }
  }

  /** The guest's answer to a hint request. Matched on seq; stale or unknown results are dropped. */
  public onDrainResult(peerId: string, result: StandingLatencyDrainResult): void {
    const episode = this.episodes.get(peerId);
    if (!episode || episode.seq !== result.seq || episode.step === null) return;
    episode.result = result;
    // A step the guest could not apply is judged now: waiting out the window would only delay
    // the next lever, and the verdict cannot move from a write that never happened.
    if (!result.applied) this.finishStep(peerId, episode, false, 'not-applied');
  }

  /** True while a drain step is in flight for the peer (Phase 4's reclaim treats that tick as not evidence). */
  public isDraining(peerId: string): boolean {
    const episode = this.episodes.get(peerId);
    return episode !== undefined && episode.step !== null;
  }

  public clearPeer(peerId: string): void {
    const episode = this.episodes.get(peerId);
    if (episode) this.closeEpisode(peerId, episode);
  }

  private beginEpisode(peerId: string, verdict: StandingLatencyVerdict, depth: DrainEpisode['latestDepth']): void {
    const episode: DrainEpisode = {
      episodeId: `${peerId}#${++this.episodeCounter}`,
      ladder: initialDrainLadderState(this.nowMs(), verdict.excessMs),
      latestVerdict: verdict,
      latestDepth: depth,
      before: snapshotOf(verdict, depth),
      step: null, seq: null, result: null, dipRestore: null, dip: null, verifyTimer: null, finished: false,
    };
    this.episodes.set(peerId, episode);
    this.takeNextStep(peerId, episode);
  }

  private closeEpisode(peerId: string, episode: DrainEpisode): void {
    this.cancelVerify(episode);
    this.restoreDip(peerId, episode);
    episode.step = null;
    episode.finished = true;
    this.episodes.delete(peerId);
  }

  private takeNextStep(peerId: string, episode: DrainEpisode): void {
    if (episode.finished) return;
    const step = nextDrainStep(episode.ladder, episode.latestVerdict.depthHypothesis, this.maxAttempts);
    if (step === null) {
      this.report(peerId, episode, null, false, null, snapshotOf(episode.latestVerdict, episode.latestDepth), 'exhausted');
      episode.finished = true;
      return;
    }
    episode.ladder = advanceDrainLadder(episode.ladder, step);
    Object.assign(episode, { step, seq: null, result: null, before: snapshotOf(episode.latestVerdict, episode.latestDepth) });
    if (step === 'host-fps-dip') this.applyFpsDip(peerId, episode);
    else this.sendHintRequest(peerId, episode, step);
  }

  private sendHintRequest(peerId: string, episode: DrainEpisode, action: DrainAction): void {
    const request: StandingLatencyDrainRequest = {
      type: STANDING_LATENCY_DRAIN_MESSAGE_TYPE,
      seq: ++this.seqCounter,
      action,
      ...(action === 'pulse' ? { pulseTargetMs: DRAIN_PULSE_TARGET_MS } : {}),
    };
    episode.seq = request.seq;
    if (!this.deps.send(peerId, JSON.stringify(request))) {
      this.finishStep(peerId, episode, false, 'not-applied');
      return;
    }
    this.armVerify(peerId, episode);
  }

  private applyFpsDip(peerId: string, episode: DrainEpisode): void {
    const current = this.deps.currentEncoding(peerId);
    const toFps = current ? nextLowerFpsRung(current.maxFramerate) : null;
    if (!current || toFps === null) {
      this.finishStep(peerId, episode, false, 'not-applied');
      return;
    }
    episode.dipRestore = { maxBitrateBps: current.maxBitrateBps, maxFramerate: current.maxFramerate };
    episode.dip = { fromFps: current.maxFramerate, toFps };
    this.deps.applyEncoding(peerId, { maxBitrateBps: current.maxBitrateBps, maxFramerate: toFps });
    this.armVerify(peerId, episode);
  }

  private armVerify(peerId: string, episode: DrainEpisode): void {
    this.cancelVerify(episode);
    episode.verifyTimer = this.schedule(() => {
      episode.verifyTimer = null;
      if (episode.step !== null) this.finishStep(peerId, episode, false);
    }, this.verifyMs);
  }

  private cancelVerify(episode: DrainEpisode): void {
    if (episode.verifyTimer !== null) this.cancel(episode.verifyTimer);
    episode.verifyTimer = null;
  }

  /** Put the exact prior encoding back after a dip; a no-op when no dip is in force. */
  private restoreDip(peerId: string, episode: DrainEpisode): void {
    const restore = episode.dipRestore;
    episode.dipRestore = null;
    if (restore) this.deps.applyEncoding(peerId, restore);
  }

  /** Judge the step in flight, report it, and either stop (success) or climb to the next rung. */
  private finishStep(peerId: string, episode: DrainEpisode, cleared: boolean, forced?: DrainOutcome): void {
    const step = episode.step;
    if (step === null) return;
    this.cancelVerify(episode);
    this.restoreDip(peerId, episode);
    const after = snapshotOf(episode.latestVerdict, episode.latestDepth);
    const standingNow = episode.latestVerdict.kind === 'standing-buffer';
    const outcome = forced ?? judgeDrainStep(episode.before.excessMs, after.excessMs, cleared || !standingNow);
    const applied = step === 'host-fps-dip' ? outcome !== 'not-applied' : episode.result?.applied ?? false;
    this.report(peerId, episode, step, applied, episode.result?.readback ?? null, after, outcome);
    Object.assign(episode, { step: null, seq: null, dip: null });
    if (isDrainSuccess(outcome)) episode.finished = true;
    else this.takeNextStep(peerId, episode);
  }

  /** One `[Drain]` line and one `drain` health event. The action IS the step: a hint name or the host dip. */
  private report(
    peerId: string,
    episode: DrainEpisode,
    step: DrainStep | null,
    applied: boolean,
    readback: ReceiverHintReadback | null,
    after: DepthSnapshot,
    outcome: DrainOutcome,
  ): void {
    const dip = episode.dip;
    this.deps.debugLog('[Drain]', {
      peerId,
      episodeId: episode.episodeId,
      step,
      attempt: episode.ladder.attempt,
      action: step,
      applied,
      readback,
      before: episode.before,
      after,
      outcome,
      ...(dip ? { fpsDip: dip } : {}),
    });
    this.deps.emitHealthEvent(
      peerId,
      'drain',
      compact({
        episodeId: episode.episodeId,
        step: step ?? undefined,
        attempt: episode.ladder.attempt,
        action: step ?? undefined,
        applied,
        beforeExcessMs: episode.before.excessMs ?? undefined,
        afterExcessMs: after.excessMs ?? undefined,
        readbackTargetMs: readback?.jitterBufferTargetMs ?? undefined,
        dipFromFps: dip?.fromFps,
        dipToFps: dip?.toFps,
        outcome,
        atMs: this.nowMs(),
      }),
    );
  }
}

/**
 * What the verdict says about the buffer at this instant. The transition seam carries the
 * verdict alone, so depth in frames / ms is not known here; the fields stay in the contract
 * shape (null) so replay tooling reads one record layout.
 */
function snapshotOf(
  verdict: StandingLatencyVerdict,
  depth: { frames: number | null; ms: number | null } = { frames: null, ms: null },
): DepthSnapshot {
  return {
    excessMs: verdict.excessMs === null ? null : Math.round(verdict.excessMs * 10) / 10,
    depthFrames: depth.frames,
    depthMs: depth.ms === null ? null : Math.round(depth.ms),
  };
}

/** Drop undefined values so the health detail omits rather than zero-fills. */
function compact(record: Record<string, number | string | boolean | undefined>): DrainHealthDetail {
  const out: DrainHealthDetail = {};
  for (const [key, value] of Object.entries(record)) if (value !== undefined) out[key] = value;
  return out;
}
